import { FileText } from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  useListProofreadingJobsQuery,
  useRerunProofreadingJobMutation,
} from '@/services/api/proofreadingService';
import {
  ProofreadingJob,
  ProofreadingJobStatus,
} from '@/services/types/proofreading-types';

import { DraftProofreadCycleStatus } from './DraftProofreadCycleStatus';
import { ProofreadingJobDetail } from './ProofreadingJobDetail';
import { ProofreadingJobsDropdown } from './ProofreadingJobsDropdown';
import { StartProofreadingJobButton } from './StartProofreadingJobButton';

interface ProofreadingViewProps {
  workspaceId: string;
  matterId: string;
}

export const ProofreadingView: React.FC<ProofreadingViewProps> = ({
  workspaceId,
  matterId,
}) => {
  const {
    data: jobs = [],
    isLoading,
    refetch,
  } = useListProofreadingJobsQuery({
    workspaceId,
    matterId,
  });

  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [rerunJob, { isLoading: isRerunning }] =
    useRerunProofreadingJobMutation();
  const hasAutoSelected = useRef(false);

  // Find the latest completed job
  const latestCompletedJob = useMemo(() => {
    if (!jobs || jobs.length === 0) return null;
    return (
      jobs.find((j) => j.status === ProofreadingJobStatus.COMPLETED) ?? jobs[0]
    );
  }, [jobs]);

  // Resolve selectedJob from selectedJobId, falling back to latest completed
  const selectedJob = useMemo(() => {
    if (selectedJobId) {
      const found = jobs.find((j) => j.id === selectedJobId);
      if (found) return found;
    }
    // Fallback to latest completed job if no selection or selection not found
    return latestCompletedJob;
  }, [jobs, selectedJobId, latestCompletedJob]);

  // Auto-select the latest completed job on initial load
  useEffect(() => {
    if (hasAutoSelected.current || !latestCompletedJob) return;
    hasAutoSelected.current = true;
    setSelectedJobId(latestCompletedJob.id);
    // Broadcast selection for editor export eligibility
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('proofreading:selected', {
          detail: { jobId: latestCompletedJob.id },
        }),
      );
    }
  }, [latestCompletedJob]);

  const handleJobCreated = useCallback(
    (jobId: string) => {
      // Refresh job list and select the new job
      refetch();
      setSelectedJobId(jobId);
    },
    [refetch],
  );

  const handleSelectJob = useCallback((job: ProofreadingJob) => {
    setSelectedJobId(job.id);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('proofreading:selected', { detail: { jobId: job.id } }),
      );
    }
  }, []);

  const handleCloseDetail = useCallback(() => {
    // Don't clear selection, just close detail view but keep showing results
    // Setting to null will fallback to latestCompletedJob via useMemo
    setSelectedJobId(null);
  }, []);

  return (
    <div className="bg-gray-50">
      <div className="mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">
                    Document Proofreading
                  </h1>
                </div>
              </div>
              {/* Job picker on the left */}
              <ProofreadingJobsDropdown
                jobs={jobs}
                onSelectJob={handleSelectJob}
                selectedJobId={selectedJob?.id}
                isLoading={isLoading}
              />
            </div>
            <div className="flex items-center gap-4">
              {selectedJob && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const newJob = await rerunJob({
                        matterId,
                        workspaceId,
                        jobId: selectedJob.id,
                      }).unwrap();
                      setSelectedJobId(newJob.id);
                      // Broadcast to editor for eligibility context
                      if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                          new CustomEvent('proofreading:selected', {
                            detail: { jobId: newJob.id },
                          }),
                        );
                      }
                      // Refresh history list
                      refetch();
                    } catch (e) {
                      // eslint-disable-next-line no-console
                      console.error('Failed to rerun proofreading job', e);
                    }
                  }}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isRerunning}
                >
                  {isRerunning ? 'Re-running…' : 'Re-run checks'}
                </button>
              )}
              <StartProofreadingJobButton
                matterId={matterId}
                workspaceId={workspaceId}
                onJobCreated={handleJobCreated}
              />
            </div>
          </div>
        </div>

        {/* Job Detail */}
        {selectedJob ? (
          <ProofreadingJobDetail
            jobId={selectedJob.id}
            workspaceId={workspaceId}
            matterId={matterId}
            onClose={handleCloseDetail}
          />
        ) : (
          <div className="space-y-6">
            <DraftProofreadCycleStatus
              matterId={matterId}
              workspaceId={workspaceId}
              onCycleComplete={(status) => {
                console.log('Cycle completed:', status);
                // Refresh jobs list to show any new proofreading results
                refetch();
              }}
            />

            {/* Empty State */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-12 text-center">
                <div className="mx-auto h-16 w-16 text-gray-300 mb-4">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select an analysis to view results
                </h3>
                <p className="text-gray-500 max-w-sm mx-auto">
                  Choose a proofreading job from the history dropdown to see
                  detailed analysis results and findings.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
